import type { AutoformattingProvider } from '@atlaskit/editor-common/provider-factory/autoformatting-provider';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

const _isBlankOrEmpty = (str) => !str || /^\s*$/.test(str);

const _sendAnalyticsEvent = (
	site: string,
	contentId: string,
	action: string,
	reason: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent | undefined,
	caller: string,
) => {
	if (createAnalyticsEvent) {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action,
				actionSubject: 'autoformat',
				actionSubjectId: 'undefinedLink autoformat',
				source: 'AutoformattingProvider',
				attributes: {
					site,
					contentId,
					reason,
					caller,
				},
			},
		}).fire();
	}
};

// rule is exported for testing purposes
export const undefinedLinkRule = (
	spaceKey: string,
	contentId: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent | undefined,
	caller: string,
) => ({
	'\\[(.*?)\\]\\(\\)': (match: string[]): Promise<ADFEntity> => {
		// If testing locally, you will need to set 'site' to an actual server such as "https://pug.jira-dev.com". Smart cards don't work with "localhost:8080".
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const site = location.origin;
		const title = match[1];

		if (_isBlankOrEmpty(title)) {
			_sendAnalyticsEvent(site, contentId, 'failed', 'empty title', createAnalyticsEvent, caller);
			return Promise.resolve({ type: '' });
		}

		if (!site || !spaceKey || !contentId) {
			_sendAnalyticsEvent(
				site,
				contentId,
				'failed',
				'missing site name, spaceKey or contentId',
				createAnalyticsEvent,
				caller,
			);
			return Promise.resolve({ type: '' });
		}

		const encodedTitle = encodeURIComponent(title);
		const url = `${site}/wiki/pages/createpage.action?spaceKey=${spaceKey}&title=${encodedTitle}&linkCreation=true&fromPageId=${contentId}`;

		_sendAnalyticsEvent(
			site,
			contentId,
			'success',
			'autoformat succeeded',
			createAnalyticsEvent,
			caller,
		);
		return Promise.resolve({
			type: 'inlineCard',
			attrs: {
				url,
			},
		});
	},
});

export const createConfluenceAutoformattingProvider = (
	spaceKey: string,
	contentId: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent | undefined,
	caller: string,
): AutoformattingProvider => ({
	getRules: () =>
		Promise.resolve({
			...undefinedLinkRule(spaceKey, contentId, createAnalyticsEvent, caller),
		}),
});
